import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import useRootTranslation from "../../../../../hooks/use-root-translation";
import { HeaderItem } from "../header";
import {
  Content,
  DotsContainer,
  HeadCell,
  ListContainer,
  OrderButton,
} from "./index.styled";
import { HiDotsVertical } from "react-icons/hi";
import Backdrop from "../../../../backdrop/backdrop";
import HomeDashboardPageQuery from "../../../../../models/home-dashboard-page-query.model";
import { useQuery } from "../../../../../hooks";
import { FaArrowDownShortWide } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

interface HeaderCellProps {
  item: HeaderItem;
  key: string;
  handleSearch?: (query: HomeDashboardPageQuery) => void;
  onClick?: () => void;
}

const HeadFilterCell: React.FC<HeaderCellProps> = ({
  item,
  key,
  handleSearch,
  onClick,
}) => {
  const { root } = useRootTranslation(
    "ranking-table.headers",
    "private-home-page"
  );
  const { root: root_filter } = useRootTranslation(
    "ranking-table.filters",
    "private-home-page"
  );
  const [query, setQuery] = useQuery<HomeDashboardPageQuery>();
  const [open, setOpen] = useState<boolean>(false);
  const [values, setValues] = useState<boolean[]>([]);
  const { t } = useTranslation("private-home-page");
  const theme = useTheme();
  const ref = React.createRef<HTMLDivElement>();
  const [negativeMargin, setNegativeMargin] = useState<string>("");

  useLayoutEffect(() => {
    const rect = ref.current?.getBoundingClientRect();

    if (rect) {
      const offscreen = rect.x + rect.width > window.innerWidth;
      if (offscreen) {
        const diff = 3 * (rect.x + rect.width - window.innerWidth);
        setNegativeMargin(`-${diff}px`);
      }
    }
  }, [ref]);

  const query_param = useMemo(() => item.query_param ?? "process_status", [
    item.query_param,
  ]);

  const handleClick = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const performSearch = useCallback(
    (query: HomeDashboardPageQuery) => {
      handleClick();
      handleSearch && handleSearch({ ...query, trackingPage: "1" });
    },
    [handleSearch, handleClick]
  );

  const updateQuery = useCallback(
    (v: boolean[], fetch?: boolean) => {
      const newQuery = { ...query };
      const list = item.filter?.filter((_, i) => v[i]).map((f) => f.value);
      if (list?.length) {
        newQuery[query_param] = list;
      } else {
        delete newQuery[query_param];
      }
      setQuery(newQuery);
      !!fetch && handleSearch && handleSearch(newQuery);
    },
    [item.filter, query_param, query, setQuery, handleSearch]
  );

  const updateValues = useCallback(
    (index: number) => {
      const newValues = [...values];
      newValues[index] = !newValues[index];
      setValues(newValues);
      updateQuery(newValues);
    },
    [values, updateQuery]
  );

  const handleClear = useCallback(() => {
    const v = values.map(() => false);
    setValues(v);
    updateQuery(v, true);
    handleClick();
  }, [values, updateQuery, handleClick]);

  useEffect(() => {
    const compare = (b: string, a?: string | string[]): boolean => {
      return Array.isArray(a) ? a.includes(b) : a === b;
    };

    if (!!item.filter) {
      setValues(item.filter.map((i) => compare(i.value, query[query_param])));
    }
  }, [item.filter, query, query_param]);

  return (
    <HeadCell width={item.width} key={key}>
      <Content>
        {root(item.value)}
        <DotsContainer>
          <button onClick={handleClick}>
            <HiDotsVertical />
          </button>
          {open && (
            <ListContainer ref={ref} negativeMargin={negativeMargin}>
              <ul>
                {item.filter?.map((filter, index) => (
                  <label>
                    <li key={`filter-item-${index}`}>
                      <input
                        type="checkbox"
                        checked={values[index]}
                        onChange={(e) => updateValues(index)}
                      />
                      {filter.label}
                    </li>
                  </label>
                ))}
                {item.orderBy && (
                  <li className="separator">
                    {t("ranking-table.filters.order-by")}
                  </li>
                )}
                {item.orderBy?.map((order, index) => (
                  <li
                    key={`order-item-${index}`}
                    onClick={() => {
                      const newQuery = { ...query, orderBy: order.value };
                      setQuery(newQuery);
                      performSearch(newQuery);
                    }}
                  >
                    <OrderButton>
                      {order.label}
                      <FaArrowDownShortWide
                        color={
                          query?.orderBy === order.value
                            ? theme.colors.primaryPurple.main
                            : theme.colors.gray400.main
                        }
                      />
                    </OrderButton>
                  </li>
                ))}
                {item.filter && (
                  <li>
                    <button
                      className="btn-filter"
                      onClick={() => {
                        performSearch({ ...query });  
                        onClick && onClick();
                      }}
                    >
                      {root_filter("filter")}
                    </button>
                  </li>
                )}
                <li className="last">
                  <button
                    className="btn-filter btn-clear"
                    onClick={() => handleClear()}
                  >
                    {root_filter("clear")}
                  </button>
                </li>
              </ul>
            </ListContainer>
          )}
        </DotsContainer>
      </Content>
      {open && (
        <Backdrop
          isShown={true}
          toggle={handleClick}
          transparent={true}
          zIndex={10}
        />
      )}
    </HeadCell>
  );
};

export default HeadFilterCell;
